/**
 * On/off feature flags for new, designer-led work (CLAUDE.md, "New feature
 * work"): every new feature ships OFF and stays off on every failure path.
 *
 * Deliberately separate from `analytics/budgetModel.ts`, which resolves one
 * multivariate flag with a payload; this is the plain boolean case. Pure — no
 * posthog-js, no config — so each failure path is unit-testable.
 */

/** Project details, reached from the link under the project header. The name is the old Settings tab's; it carried over. */
export const PROJECT_SETTINGS_FLAG = 'project-settings';

export const FEATURE_FLAGS = [PROJECT_SETTINGS_FLAG] as const;
export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

const FLAG_TIMEOUT_MS = 1500;

/** The slice of the PostHog client the resolver needs — injectable in tests. */
export type BooleanFlagClient = {
  onFeatureFlags: (callback: () => void) => void;
  getFeatureFlag: (key: string) => string | boolean | undefined;
};

/**
 * ON only when PostHog loaded its flags in time AND the flag is exactly `true`.
 * No client, a timeout, a missing flag, `false`, or a multivariate string
 * variant all read as off.
 */
export const resolveFlagFrom = async (
  client: BooleanFlagClient | null,
  key: FeatureFlag,
  timeoutMs: number = FLAG_TIMEOUT_MS,
): Promise<boolean> => {
  if (!client) return false;

  const flagsLoaded = new Promise<'loaded'>((resolve) => {
    client.onFeatureFlags(() => resolve('loaded'));
  });
  const timedOut = new Promise<'timeout'>((resolve) => {
    setTimeout(() => resolve('timeout'), timeoutMs);
  });
  if ((await Promise.race([flagsLoaded, timedOut])) === 'timeout') return false;

  return client.getFeatureFlag(key) === true;
};

/**
 * Local builds only: which flags are forced on, so a feature can be seen
 * without PostHog (which never starts locally — `.env` has no key).
 *
 * Set from the address bar: `?flags=project-settings` turns that flag on and
 * remembers it in this browser; `?flags=` turns every forced flag off again.
 */
export const FLAG_OVERRIDE_KEY = 'pm.devTools.flags';
const FLAGS_PARAM = 'flags';

const isFeatureFlag = (value: string): value is FeatureFlag =>
  (FEATURE_FLAGS as readonly string[]).includes(value);

/** The forced-on list a `?flags=` value names, or `null` when the URL has none. */
export const parseFlagsParam = (search: string): FeatureFlag[] | null => {
  const raw = new URLSearchParams(search).get(FLAGS_PARAM);
  if (raw === null) return null;
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(isFeatureFlag);
};

/** The stored forced-on list; anything unreadable counts as none. */
export const parseStoredOverrides = (raw: string | null): FeatureFlag[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter(
          (item): item is FeatureFlag => typeof item === 'string' && isFeatureFlag(item),
        )
      : [];
  } catch {
    return [];
  }
};
